import * as echarts from "echarts";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Measure from "react-measure";
import { useDispatch, useSelector, useStore } from "react-redux";
import { binarySearch, boundedBinarySearch } from "../util/PlaylistUtils";
import useELRBoundaries from "./ChartV2/elrBoundaries";
import useLineSpeeds from "./ChartV2/lineSpeeds";
import useOverview from "./ChartV2/overview";
import useDataSeries from "./ChartV2/series";
import {
    asyncLoadGeometryFile,
    boundedRange,
    boundedValue,
    calculateGrid,
    downloadTrackGeometryHeaders,
    geometryDataSelector,
    useCombinedOptions,
} from "./ChartV2/utils";
import { comparisonData, elrBoundaries, exceedences, lineSpeeds, nearbyData } from "./ChartV2DataFilter";
import ComparisonGraphAlignment from "./ChartV2/ComparisonGraphAlignment";

echarts.registerTransform(nearbyData);
echarts.registerTransform(comparisonData);
echarts.registerTransform(elrBoundaries);
echarts.registerTransform(lineSpeeds);
echarts.registerTransform(exceedences);

const deviceConfigSelector = (state) => {
    return state.railInspection.railInspectionImageConfig;
};

const sessionDataSelector = (state) => {
    const dashboardData = _.get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state"], {});
    return _.get(state.sessions, [dashboardData.routeID]);
};

const geomHeadersSelector = (state) => state.trackGeometry.trackGeometryHeaders;
const offsetSelector = (state) => state.trackGeometry.comparisonOffset;

const ChartV2 = ({
    selectedTimestamp,
    dataTypes,
    colorScheme,
    exceedenceSpeed,
    elrData,
    poiData,
    lastUpdate,
    onSelectTimestamp,
    dataFiles,
    comparisonSession,
    indexSettersRef,
    chartHandleRef,
    displayPoints,
    displayMLDetections,
}) => {
    const store = useStore();
    const dispatch = useDispatch();

    const geometryData = useSelector(geometryDataSelector);
    const sessionData = useSelector(sessionDataSelector);
    const geomHeaders = useSelector(geomHeadersSelector);
    const { horizontal: comparisonOffset, vertical: verticalComparisonOffset } = useSelector(offsetSelector);

    const [chart, setChart] = useState(null);
    const [zoomBounds, setZoomBounds] = useState([0, 8200]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [lastChartOptions, setLastChartOptions] = useState({});

    const chartRef = useRef();
    const maxBoundsRef = useRef(0);
    const recentZoomBoundsRef = useRef(null);

    const [comparisonSessionHeaders, setComparisonSessionHeaders] = useState([]);
    const [loadedComparisonSession, setLoadedComparisonSession] = useState(null);
    const [comparisonSessionLoading, setComparisonSessionLoading] = useState(false);

    const railInspectionConfig = useSelector(deviceConfigSelector);

    useEffect(() => {
        const ref = indexSettersRef.current;
        ref["ChartV2"] = setSelectedIndex;

        return () => {
            ref["ChartV2"] = null;
        };
    }, [indexSettersRef]);

    const updateIndex = useCallback(
        (newIndex) => {
            for (const setter of Object.values(indexSettersRef.current)) {
                if (!setter) continue;
                setter(newIndex);
            }
        },
        [indexSettersRef],
    );

    const indexOffset = useMemo(() => {
        return _.get(railInspectionConfig, ["alignment_offsets", "geometry_index_offset"], 0);
    }, [railInspectionConfig]);

    const imageryIsBackward = useMemo(() => {
        if (!sessionData) {
            return false;
        }
        const cameraAtRear = _.get(railInspectionConfig, ["video_orientation"]) === "Rear";
        const sessionIsBackward = _.indexOf(sessionData.tags, "Backward") !== -1;

        return sessionIsBackward !== cameraAtRear;
    }, [railInspectionConfig, sessionData]);

    useEffect(() => {
        if (dataFiles) {
            const dataForTimestamp = dataFiles.find((_f) => _f.endTS >= selectedTimestamp && _f.startTS <= selectedTimestamp);
            if (dataForTimestamp) {
                asyncLoadGeometryFile(dataForTimestamp, store, dispatch).then((allData) => {
                    const lowIndex = dataForTimestamp.start;
                    const highIndex = dataForTimestamp.end;
                    const nearestIndex = boundedBinarySearch(selectedTimestamp, allData, lowIndex, highIndex, (geom) => geom.timestamp);
                    const offsettedIndex = !imageryIsBackward
                        ? Math.max(0, nearestIndex - indexOffset)
                        : Math.min(allData.length - 1, nearestIndex + indexOffset);

                    updateIndex(offsettedIndex);
                });
            }
        } else {
            updateIndex(binarySearch(selectedTimestamp, geometryData, (geom) => geom.timestamp));
        }
    }, [selectedTimestamp, geometryData, dataFiles, store, dispatch, railInspectionConfig, indexOffset, imageryIsBackward, updateIndex]);

    useEffect(() => {
        if (comparisonSession === loadedComparisonSession) return;

        setComparisonSessionHeaders([]);

        if (!comparisonSession) return;

        setComparisonSessionLoading(true);
        downloadTrackGeometryHeaders(comparisonSession, store, dispatch).then((headers) => {
            let offset = 0;
            const comparison_headers = headers
                .map((d) => {
                    const mile_sign = d.mile_and_eighth < 0 ? -1 : 1;
                    const mile = Math.floor(Math.abs(d.mile_and_eighth));
                    const chain = (Math.abs(d.mile_and_eighth) - mile) * 100;
                    const position = Math.round(mile_sign * (mile * 80 + chain));
                    const start_offset = offset;
                    offset += d.record_count;
                    const end_offset = offset - 1;
                    return {
                        elr: d.elr === null || d.elr === "unk" ? "?" : d.elr,
                        track: d.track_id === null || d.track_id === "unk" ? "?" : d.track_id,
                        start: start_offset,
                        end: end_offset,
                        position,
                        filename: d.filename,
                        record_count: d.record_count,
                    };
                })
                .filter((header) => dataFiles.filter((f) => f.elr === header.elr && f.track === header.track && f.position === header.position).length > 0);

            setComparisonSessionLoading(false);
            setComparisonSessionHeaders(comparison_headers);
            setLoadedComparisonSession(comparisonSession);
        });
    }, [comparisonSession, loadedComparisonSession, dispatch, store, dataFiles]);

    const palette = useMemo(() => {
        return {
            darkMode: colorScheme === "dark",
            elrBoundary: colorScheme === "dark" ? "#caf" : "#60c",
            xZero: colorScheme === "dark" ? "#aaa" : "#666",
            yZero: colorScheme === "dark" ? "#caf" : "#60c",
            selected: colorScheme === "dark" ? "#f44" : "#c00",
            div: colorScheme === "dark" ? "#666" : "#aaa",
            bg: colorScheme === "dark" ? "#2f334d" : "#fff",
            line: colorScheme === "dark" ? "#fff" : "#000",
            comparisonLine: colorScheme === "dark" ? "#9191ff" : "#3838ff",
            lineHighlight: colorScheme === "dark" ? "#8f8" : "#070",
            lineSelect: colorScheme === "dark" ? "#0f0" : "#0c0",
            lineWidth: colorScheme === "dark" ? 2 : 1,
            label: colorScheme === "dark" ? "#fff" : "#000",
            poiLabel: colorScheme === "dark" ? "#fff" : "#000",
            overviewLabel: colorScheme === "dark" ? "#000" : "#fff",
            overviewBorder: colorScheme === "dark" ? "#000" : "#fff",
            speedMinR: colorScheme === "dark" ? 230 : 150,
            speedMinG: colorScheme === "dark" ? 255 : 200,
            speedMinB: colorScheme === "dark" ? 190 : 100,
            speedMaxR: colorScheme === "dark" ? 100 : 80,
            speedMaxG: colorScheme === "dark" ? 60 : 0,
            speedMaxB: colorScheme === "dark" ? 155 : 120,
            exceedence: colorScheme === "dark" ? "#f88" : "#f00",
            currentViewFill: "rgba(220,192,255,0.5)",
            currentViewBorder: "rgba(220,192,255,0.9)",
        };
    }, [colorScheme]);

    useEffect(() => {
        const new_chart = echarts.init(chartRef.current);
        setChart(new_chart);
        return () => {
            new_chart.dispose();
        };
    }, []);

    useEffect(() => {
        if (!chart) return;

        chart.resize();
    }, [chart, displayPoints]);

    const viewBounds = useCallback(
        (bounds) => {
            if (chart) {
                const options = chart.getOption();
                const xAxes = options.xAxis;
                const newAxisBounds = xAxes
                    .filter((axis) => axis.id !== "overview_x")
                    .map((axis) => ({
                        id: axis.id,
                        min: bounds[0],
                        max: bounds[1],
                    }));

                const minHighlight = boundedValue(0, bounds[0], maxBoundsRef.current);
                const maxHighlight = boundedValue(0, bounds[1], maxBoundsRef.current);
                chart.setOption({
                    xAxis: newAxisBounds,
                    series: [
                        {
                            id: "current_view_series",
                            data: [[minHighlight, maxHighlight]],
                        },
                    ],
                });
            }
        },
        [chart],
    );

    const selectIndex = useCallback(
        (index) => {
            if (index !== undefined) {
                const offsettedIndex = imageryIsBackward ? Math.max(0, index - indexOffset) : index + indexOffset;

                if (dataFiles) {
                    const dataForIndex = dataFiles.find((_f) => _f.end >= offsettedIndex && _f.start <= offsettedIndex);
                    if (dataForIndex) {
                        asyncLoadGeometryFile(dataForIndex, store, dispatch).then((allData) => {
                            onSelectTimestamp(allData[offsettedIndex].timestamp);
                        });
                    }
                } else {
                    if (0 <= offsettedIndex && offsettedIndex <= maxBoundsRef.current) {
                        onSelectTimestamp(geometryData[offsettedIndex].timestamp);
                    }
                }
            }
        },
        [imageryIsBackward, indexOffset, dataFiles, store, dispatch, onSelectTimestamp, geometryData],
    );

    const selectBounds = useCallback(
        (bounds) => {
            console.log("Selecting bounds: ", bounds);
            setZoomBounds(bounds);
            viewBounds(bounds);
        },
        [viewBounds],
    );

    useEffect(() => {
        if (elrData.length) {
            const lastELRDataLength = maxBoundsRef.current;
            maxBoundsRef.current = elrData[elrData.length - 1].end;
            if (lastELRDataLength === 0 && maxBoundsRef.current > 0) {
                selectBounds([0, 8200]);
            }
        } else {
            maxBoundsRef.current = 0;
        }
    }, [elrData, selectBounds]);

    const usingDataFiles = !!(dataFiles && dataFiles.length);

    const seriesScales = useMemo(() => {
        return dataTypes.map((dataType) => {
            const header = _.find(geomHeaders, { header_name: dataType });
            const header_type = header.name.split("_")[0] !== "DJ" ? header.name : "DJ";
            const scale = header.info.scale;
            const minTick = Math.floor(parseFloat(header.info.min) / scale);
            const maxTick = Math.ceil(parseFloat(header.info.max) / scale);
            return {
                type: usingDataFiles ? header.name : dataType,
                uniq_type: usingDataFiles ? header_type : dataType,
                name: header.name,
                displayName: header.info.display_name,
                scale: scale,
                units: header.unit,
                minTick: minTick,
                maxTick: maxTick,
                ticks: maxTick - minTick,
                minScale: minTick * scale,
                maxScale: maxTick * scale,
            };
        });
    }, [dataTypes, geomHeaders, usingDataFiles]);

    const dynamicGrid = useMemo(
        () =>
            calculateGrid(seriesScales, {
                fontSize: 12,
                padding: 10,
            }),
        [seriesScales],
    );

    const chartInfo = useMemo(
        () => ({
            chart,
            palette,
            elrData,
            poiData,
            zoomBounds,
            maxBoundsRef,
            viewBounds,
            selectBounds,
            selectIndex,
            comparisonSessionHeaders,
            usingDataFiles,
            dataTypes,
            exceedenceSpeed,
            comparisonOffset,
            verticalComparisonOffset,
            chartHandleRef,
            selectedIndex,
            displayPoints,
            seriesScales,
            dynamicGrid,
            displayMLDetections,
        }),
        [
            chart,
            palette,
            elrData,
            poiData,
            zoomBounds,
            viewBounds,
            selectBounds,
            selectIndex,
            comparisonSessionHeaders,
            usingDataFiles,
            dataTypes,
            exceedenceSpeed,
            comparisonOffset,
            verticalComparisonOffset,
            chartHandleRef,
            selectedIndex,
            displayPoints,
            seriesScales,
            dynamicGrid,
            displayMLDetections,
        ],
    );

    const overviewOptions = useOverview(chartInfo);
    const seriesOptions = useDataSeries(chartInfo);
    const elrBoundaryOptions = useELRBoundaries(chartInfo);
    const lineSpeedOptions = useLineSpeeds(chartInfo);
    const colourOptions = useMemo(
        () => ({
            backgroundColor: [palette.bg],
            darkMode: [palette.darkMode],
            axisPointer: [
                {
                    link: [
                        {
                            xAxisId: ["overview_x", "elr_boundary_x"],
                        },
                    ],
                },
            ],
        }),
        [palette],
    );

    const [chartOptions, changedOptions] = useCombinedOptions([overviewOptions, seriesOptions, elrBoundaryOptions, lineSpeedOptions, colourOptions]);

    useEffect(() => {
        if (chart) {
            let clearBeforeSet = false;

            if (!Object.is(chartOptions.grid, lastChartOptions.grid)) {
                clearBeforeSet = true;
            }

            if (clearBeforeSet) {
                chart.setOption({}, { notMerge: true });

                const minHighlight = boundedValue(0, zoomBounds[0], maxBoundsRef.current);
                const maxHighlight = boundedValue(0, zoomBounds[1], maxBoundsRef.current);

                chartOptions.series.find((series) => series.id === "current_view_series").data = [[minHighlight, maxHighlight]];
                chartOptions.series.find((series) => series.id === "selected_index_main_series").data = [[selectedIndex]];
                chartOptions.series.find((series) => series.id === "selected_index_overview_series").data = [[selectedIndex]];

                chart.setOption(chartOptions);
            } else {
                chart.setOption(changedOptions, { lazyUpdate: true });
            }

            setLastChartOptions(chartOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, chartOptions]);

    useEffect(() => {
        if (chart) {
            recentZoomBoundsRef.current = zoomBounds;
            if (dataFiles) {
                const visibleData = dataFiles.filter((_f) => _f.end >= zoomBounds[0] && _f.start <= zoomBounds[1]);
                visibleData.forEach((data) => asyncLoadGeometryFile(data, store, dispatch));
            }
        }
    }, [zoomBounds, dataFiles, store, dispatch, chart]);

    useEffect(() => {
        if (chart) {
            chart.setOption({
                series: [
                    {
                        id: "selected_index_main_series",
                        data: [[selectedIndex]],
                    },
                    {
                        id: "selected_index_overview_series",
                        data: [[selectedIndex]],
                    },
                ],
            });

            if (selectedIndex < recentZoomBoundsRef.current[0] || selectedIndex > recentZoomBoundsRef.current[1]) {
                const current_delta = (recentZoomBoundsRef.current[1] - recentZoomBoundsRef.current[0]) / 2;
                const newBounds = boundedRange(0, Math.floor(selectedIndex - current_delta), Math.floor(selectedIndex + current_delta), maxBoundsRef.current);

                console.log("Setting zoom bounds based on selected index: ", newBounds, selectedIndex);
                selectBounds(newBounds);
            }
        }
    }, [chart, selectedIndex, selectBounds]);

    useEffect(() => {
        if (chart) {
            chart.setOption({
                dataset: [
                    {
                        id: "nearby_dataset",
                        source: [lastUpdate],
                    },
                ],
            });
        }
    }, [lastUpdate, chart]);

    const onResize = useCallback(() => {
        if (chart) {
            chart.resize();
        }
    }, [chart]);

    return (
        <Measure
            bounds
            onResize={onResize}>
            {({ measureRef }) => (
                <div
                    className="widget__Chart__Inner"
                    style={{ backgroundColor: colorScheme === "light" ? "white" : "" }}
                    ref={measureRef}>
                    <ComparisonGraphAlignment comparisonSession={comparisonSession} />
                    <div
                        className={`widget__Chart ${comparisonSessionLoading && "animate-pulse"}`}
                        ref={chartRef}
                    />
                </div>
            )}
        </Measure>
    );
};

export default ChartV2;
